import React from "react";
import { useTranslation } from "react-i18next";
import {
  CARD_TITLE_SIZE_CLASSES,
  TYPOGRAPHY_ROLE_CLASSES,
} from "@/utils/typographyTokens";

interface SiteUnavailableStateProps {
  reason?: "offline" | "suspended" | "terms_violation" | "maintenance";
  customMessage?: string;
}

const SiteUnavailableState: React.FC<SiteUnavailableStateProps> = ({
  reason = "offline",
  customMessage,
}) => {
  const { t } = useTranslation();
  const getMessage = () => {
    if (customMessage) return customMessage;

    switch (reason) {
      case "suspended":
        return t("landing.common.siteUnavailable.suspended");
      case "terms_violation":
        return t("landing.common.siteUnavailable.termsViolation");
      case "maintenance":
        return t("landing.common.siteUnavailable.maintenance");
      case "offline":
      default:
        return t("landing.common.siteUnavailable.offline");
    }
  };

  const getIcon = () => {
    switch (reason) {
      case "suspended":
      case "terms_violation":
        return (
          <svg
            className="w-20 h-20 text-gray-300 mb-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        );
      case "maintenance":
        return (
          <svg
            className="w-20 h-20 text-gray-300 mb-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        );
      case "offline":
      default:
        return (
          <svg
            className="w-20 h-20 text-gray-300 mb-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 px-4 py-16">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 sm:p-12">
          {/* Icon */}
          <div className="flex justify-center">{getIcon()}</div>

          {/* Title */}
          <h1 className={`${CARD_TITLE_SIZE_CLASSES.xl} font-bold text-gray-900 text-center mb-4`}>
            {t("landing.common.siteUnavailable.title")}
          </h1>

          {/* Message */}
          <p className={`${TYPOGRAPHY_ROLE_CLASSES.body} text-gray-600 text-center leading-relaxed mb-8`}>
            {getMessage()}
          </p>

          {/* Decorative line */}
          <div className="flex items-center gap-3 mb-8">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
            <div className="w-2 h-2 rounded-full bg-gray-300" />
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
          </div>

          {/* Additional info */}
          <div className="space-y-3">
            <div className={`flex items-start gap-3 ${TYPOGRAPHY_ROLE_CLASSES.body} text-gray-500`}>
              <svg
                className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="leading-relaxed">
                {t("landing.common.siteUnavailable.ownerPrompt")}
              </span>
            </div>
          </div>

          {/* Footer note */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <p className={`${TYPOGRAPHY_ROLE_CLASSES.body} text-gray-400 text-center`}>
              {t("landing.common.siteUnavailable.errorCode", {
                code: reason.toUpperCase().replace("_", "-"),
              })}
            </p>
          </div>
        </div>

        {/* Optional background decoration */}
        <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-50 rounded-full blur-3xl opacity-30 transform translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-50 rounded-full blur-3xl opacity-30 transform -translate-x-1/2 translate-y-1/2" />
        </div>
      </div>
    </div>
  );
};

export default SiteUnavailableState;
